import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { withBase } from '../utils/apiBase';
import { getOptimizedBlogImage } from '../utils/cloudinary';
import { getCachedBlogs, setCachedBlogs } from '../utils/blogCache';

const TYPE_COLORS = {
  Event:      'bg-forest/15 text-forest',
  Garden:     'bg-gold/15 text-gold',
  Fundraiser: 'bg-flag_red/10 text-flag_red_deep',
};

const getImageUrl = (imagePath, size = 'medium') => {
  if (!imagePath) return withBase('/oli-logo.png');
  if (imagePath.startsWith('http')) return getOptimizedBlogImage(imagePath, size);
  const normalizedPath = imagePath.replace(/\\/g, '/').replace(/^\/?/, '/');
  return withBase(normalizedPath);
};

function BlogSlider() {
  const [centerIndex, setCenterIndex] = useState(0);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sliderRef = useRef(null);
  const imageErrorRefs = useRef(new Set());

  useEffect(() => {
    const cached = getCachedBlogs('blogs-all-home');
    if (cached) {
      setBlogPosts(cached.slice(0, 6));
      setLoading(false);
      return;
    }
    fetch(withBase('/blogs'))
      .then(res => res.json())
      .then(data => {
        const posts = Array.isArray(data) ? data.slice(0, 6) : [];
        setCachedBlogs('blogs-all-home', posts);
        setBlogPosts(posts);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch blog posts:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const settings = {
    className: 'center',
    centerMode: true,
    infinite: true,
    centerPadding: '60px',
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    dots: true,
    autoplay: true,
    autoplaySpeed: 5000,
    swipeToSlide: true,
    focusOnSelect: true,
    beforeChange: (_, next) => setCenterIndex(next),
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 1, centerMode: true, centerPadding: '40px', dots: true, autoplay: true, autoplaySpeed: 5000 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 1, centerMode: false, centerPadding: '0px', dots: true, autoplay: true, autoplaySpeed: 5000 },
      },
    ],
  };

  if (loading) {
    return (
      <div className="py-16 px-6 bg-forest text-center">
        <div className="flex items-center justify-center gap-2 text-cream/70">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-sm font-semibold">Loading stories…</span>
        </div>
      </div>
    );
  }

  if (error || blogPosts.length === 0) {
    return (
      <div className="py-16 px-6 bg-forest text-center">
        <p className="text-cream/60 text-sm">No posts to display yet.</p>
      </div>
    );
  }

  return (
    <div className="relative bg-forest pb-12 pt-4">
      {/* Prev arrow */}
      <button
        onClick={() => sliderRef.current?.slickPrev()}
        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full border border-gold/30 bg-forest_deep/60 backdrop-blur-sm flex items-center justify-center hover:border-gold hover:bg-forest_deep transition-colors duration-200"
        aria-label="Previous post"
      >
        <svg className="w-5 h-5 text-gold_soft" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
      </button>

      {/* Next arrow */}
      <button
        onClick={() => sliderRef.current?.slickNext()}
        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full border border-gold/30 bg-forest_deep/60 backdrop-blur-sm flex items-center justify-center hover:border-gold hover:bg-forest_deep transition-colors duration-200"
        aria-label="Next post"
      >
        <svg className="w-5 h-5 text-gold_soft" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>

      <Slider ref={sliderRef} {...settings}>
        {blogPosts.map((post, idx) => {
          const isCenter = idx === centerIndex;
          const imgKey = `${post.slug}-${isCenter ? 'c' : 's'}`;

          return (
            <div key={post.slug} className="px-2 py-4">
              <Link
                to={`/blogs/${post.slug}`}
                className={`block rounded-card overflow-hidden border transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
                  isCenter
                    ? 'border-gold/40 shadow-card-hover scale-100'
                    : 'border-white/10 opacity-60 hover:opacity-80 scale-95'
                }`}
                style={{ transform: isCenter ? 'scale(1)' : 'scale(0.95)' }}
                tabIndex={isCenter ? 0 : -1}
              >
                {/* Image */}
                <div className="relative w-full overflow-hidden" style={{ height: isCenter ? '200px' : '160px' }}>
                  <img
                    src={getImageUrl(post.image, isCenter ? 'large' : 'medium')}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    onError={(e) => {
                      if (!imageErrorRefs.current.has(imgKey)) {
                        imageErrorRefs.current.add(imgKey);
                        e.target.src = withBase('/oli-logo.png');
                      }
                    }}
                  />
                  {/* Type badge */}
                  <span className={`absolute top-3 left-3 text-[11px] font-bold uppercase tracking-label px-2.5 py-1 rounded-full ${TYPE_COLORS[post.type] || TYPE_COLORS.Event}`}>
                    {post.type}
                  </span>
                </div>

                {/* Card body */}
                <div className="bg-white p-4">
                  <h3 className="font-display font-bold text-ink text-sm sm:text-base leading-snug line-clamp-2 mb-1.5">
                    {post.title}
                  </h3>
                  {isCenter && (
                    <p className="text-ink_soft text-xs leading-relaxed line-clamp-2 mb-3">
                      {post.excerpt || post.content?.slice(0, 90) + '…'}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-xs text-ink_soft/70">
                      {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    {isCenter && (
                      <span className="text-xs font-bold text-forest uppercase tracking-label flex items-center gap-1">
                        Read
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
      </Slider>
    </div>
  );
}

export const BlogMenu = () => (
  <BlogSlider />
);
